import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { IWorkflowRequest } from 'src/app/models/requests/WorkflowRequest';
import { DialogRequestDetailsComponent } from '../dialog-request-details/dialog-request-details.component';
import { SelectionModel } from '@angular/cdk/collections';
import { matTableSelectionHelper } from 'src/app/helpers/matTableSelectionHelper';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { WorkflowRequestTypes } from 'src/app/models/requests/enums/WorkflowRequestTypes.enum';
import { CampService } from 'src/app/services/campService/campService';
import { CalAngularService } from '@cvx/cal-angular';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CampEntity } from 'src/app/models/campEntity';
import { CompromisedSourcingCompanyCriteriaType } from 'src/app/models/cyber/enums/CyberEventCriteriaType';
import { CreateCyberEventCompromisedSourcingCompanyRequest } from 'src/app/models/cyber/CyberEventCompromisedSourcingCompanyRequest';
import { IDirectoryUser } from 'src/app/models/directory/users/DirectoryUser';
import { DialogDirectorySearchComponent } from '../dialog-directory-search/dialog-directory-search.component';
import { GroupMemberType } from 'src/app/models/directory/enums/GroupMemberType.enum';
import { DirectoryDomain } from 'src/app/models/directory/enums/DirectoryDomain.enum';
import { UserType } from 'src/app/models/directory/enums/UserType.enum';
import { IDialogDirectoryData } from 'src/app/models/components/IDialogDirectoryData';
import { ToStringHelper } from 'src/app/helpers/ToStringHelper';

@Component({
    selector: 'app-workflow-request-display',
    styleUrl: 'workflow-request-display.component.css',
    templateUrl: './workflow-request-display.component.html',
    animations: [
        trigger('detailExpand', [
            state('collapsed,void', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ]
})
export class WorkflowRequestDisplayComponent implements OnInit, OnChanges {
    @ViewChild(MatPaginator, { static: false })
    set paginator(value: MatPaginator) {
        this.requestsDataSource.paginator = value;
    }
    @ViewChild(MatSort, { static: false })
    set sort(value: MatSort) {
        this.requestsDataSource.sort = value;
    }

    @Input() requests: IWorkflowRequest[] = [];
    @Input() includeCheckbox: boolean = false;
    @Input() includeFilter: boolean = false;
    @Input() includeDelete: boolean = false;
    @Input() includePaginator: boolean = false;
    @Input() showRequestorPicker: boolean = false;
    @Input() selectedRequestor: IDirectoryUser | undefined = undefined;
    @Input() selectedRequests: IWorkflowRequest[] = [];
    @Output() selectedRequestsChange: EventEmitter<IWorkflowRequest[]> = new EventEmitter<IWorkflowRequest[]>();
    @Output() requestorSelectionChanged: EventEmitter<IDirectoryUser> = new EventEmitter<IDirectoryUser>();

    coreCols: string[] = ['id', 'type', 'state', 'createDate'];
    displayCols: string[] = [...this.coreCols, 'displayName', 'expand'];
    requestsDataSource = new MatTableDataSource<IWorkflowRequest>([]);
    selection = new SelectionModel<IWorkflowRequest>(true, []);
    expandedElement: IWorkflowRequest | null;
    populatingHiddenCol = true;

    constructor(private dialog: MatDialog, private campService: CampService, private authService: CalAngularService) { }
    ngOnChanges(changes: SimpleChanges): void {
        this.expandedElement = null;
        if (changes.requests) {
            this.requestsDataSource.data = this.requests;
        }
        if (changes.selectedRequests) {
            this.selection.setSelection(...this.selectedRequests);
        }
        if (changes.selectedRequestor) {
            this.selectedRequestor = changes.selectedRequestor.currentValue;
        }
    }

    async ngOnInit(): Promise<void> {
        this.expandedElement = null;
        this.requestsDataSource.paginator = this.paginator
        this.requestsDataSource.sort = this.sort;
        this.requestsDataSource.data = this.requests;
        await this.populateHiddenSearchableColumns();
        this.displayCols = [...this.coreCols, 'displayName', 'expand'];
        if (this.includeCheckbox) {
            this.displayCols = ['select', ...this.displayCols];
        }
        if (this.includeDelete) {
            this.displayCols = [...this.displayCols, 'del']
        }
    }

    filter = (value: any) => {
        this.requestsDataSource.filter = value.target.value.trim().toLocaleLowerCase();
    }

    async populateHiddenSearchableColumns() {
        this.populatingHiddenCol = true;
        await Promise.all(this.requestsDataSource.data.map(async r => r.displayName = await this.getHiddenColValue(r)));
        this.populatingHiddenCol = false;
    }

    async getHiddenColValue(request: IWorkflowRequest): Promise<string> {
        switch (this.getWorkflowRequestType(request.type)) {
            case WorkflowRequestTypes.CyberEventCompromisedSourcingCompanyCreate:
                let requestDetails = request.requestDetails as CreateCyberEventCompromisedSourcingCompanyRequest;
                let ids = Array.from(new Set(requestDetails.criteria.filter(c => c.criteriaType === CompromisedSourcingCompanyCriteriaType.SourcingCompanyId).map(c => `id=${c.value}`)));
                if (ids.length === 0) {
                    return requestDetails.criteria.map(c => c.value).join(" ");
                }

                try {
                    const token = await lastValueFrom(this.authService.getAADToken(environment.tokenScopes));
                    const searchResults = await lastValueFrom(this.campService.GetCampEntity(ids.join("&"), token));
                    return requestDetails.criteria.filter(c => c.criteriaType === CompromisedSourcingCompanyCriteriaType.EmailDomain)
                        .map(c => c.value)
                        .concat((searchResults as CampEntity[]).map(e => e.name))
                        .join(" ");
                } catch (err) {
                    return '';
                }
            default:
                return '';
        }

    }

    selectRow(row: IWorkflowRequest) {
        // open dialog with request details
        this.dialog.open(DialogRequestDetailsComponent, {
            disableClose: true,
            autoFocus: true,
            maxWidth: 1000,
            width: '100%',
            data: { workflowRequest: row }
        });
    }

    deleteRow(row: IWorkflowRequest) {
        this.requestsDataSource.data = [...this.requestsDataSource.data.filter(g => g.id != row.id)];
        this.selection.deselect(row);
        this.selectedRequestsChange.emit(this.selection.selected);
    }

    select(row: IWorkflowRequest) {
        this.selection.toggle(row);
        this.selectedRequestsChange.emit(this.selection.selected);
    }

    //for checkbox select all use
    isAllSelected() {
        return matTableSelectionHelper.isAllSelected(this.selection, this.requestsDataSource);
    }
    masterToggle() {
        matTableSelectionHelper.toggleSelectAll(this.selection, this.requestsDataSource);
        this.selectedRequestsChange.emit(this.selection.selected);
    }

    getWorkflowRequestType(type: string): WorkflowRequestTypes {
        return WorkflowRequestTypes[type as keyof typeof WorkflowRequestTypes] ?? WorkflowRequestTypes.None;
    }

    openRequestInNewTab(row: IWorkflowRequest) {
        window.open(`/requests/${row.type}/${row.id}`, '_blank');
    }

    searchForRequestorUsers() {
        const dialogRef = this.dialog.open(DialogDirectorySearchComponent, {
            disableClose: true,
            autoFocus: true,
            maxWidth: 1000,
            width: '100%',
            data: {
                selectableTypesWhenNoTypeProvided: [GroupMemberType.User, GroupMemberType.ServicePrincipal],
                domain: DirectoryDomain.Chevron,
                userType: UserType.Primary,
                filterGroupRemoveNotAvailableAsGroupMember: false,
                filterGroupRemoveDynamicMembershipEnabled: false,
                filterGroupOnlyManagedByIdamp: false
            } as IDialogDirectoryData
        });

        dialogRef.afterClosed().subscribe((result: IDirectoryUser) => {
            if (result) {
                // override toString method to display displayName (as form control value will display [object Object] without this)
                result = ToStringHelper.overrideToString(result);
                this.requestorSelectionChanged.emit(result);
            }
        });
    }
}
